import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axiosInstance from '../../utils/axiosConfig';
import LoaderInside from '../LoaderInside';
import '../../scss/components/FAQPage/FAQContent.scss';

const FAQCreate = () => {
    const { category } = useParams();
    const navigate = useNavigate();
    const adminMode = useSelector((state) => state.adminMode.adminMode);

    const [loading, setLoading] = useState(false);
    const [text, setText] = useState('');
    const [highlights, setHighlights] = useState([]);
    const [selection, setSelection] = useState(null);
    const [showToolbar, setShowToolbar] = useState(false);
    const [toolbarPosition, setToolbarPosition] = useState({ top: 0, left: 0 });
    const textAreaRef = useRef(null);
    const [faqs, setFaqs] = useState([]);

    useEffect(() => {
        const fetchFaqs = async () => {
            try {
                const response = await axiosInstance.get(`/faq/${category}`);
                setFaqs(response.data);
            } catch (error) {
                console.error('Ошибка при загрузке FAQ:', error);
            }
        };

        fetchFaqs();
    }, [category]);

    // Если пользователь не админ, перенаправляем его
    if (!adminMode) {
        navigate(`/faq/${category}`);
        return null;
    }

    // Функция для определения заголовка страницы по категории
    const getCategoryTitle = () => {
        switch (category) {
            case 'rules': return 'Правила игры';
            case 'learning': return 'Всё об обучении';
            case 'tasks': return 'Как выполнять задания';
            case 'prizes': return 'Как выйграть приз';
            default: return 'FAQ';
        }
    };

    // Функция для возврата на страницу FAQ
    const handleBack = () => {
        navigate(`/faq/${category}`);
    };

    // Функция для обновления текста
    const updateText = (newText) => {
        setText(newText);
    };

    // Функция для добавления маркера списка в начало строки
    const addListItem = () => {
        const textarea = textAreaRef.current;
        if (!textarea) return;

        const { selectionStart } = textarea;
        const currentText = textarea.value;

        // Находим начало текущей строки
        let lineStart = selectionStart;
        while (lineStart > 0 && currentText[lineStart - 1] !== '\n') {
            lineStart--;
        }

        // Добавляем маркер в начало строки
        const beforeMarker = currentText.substring(0, lineStart);
        const afterMarker = currentText.substring(lineStart);
        const newText = beforeMarker + '• ' + afterMarker;

        setText(newText);

        // Устанавливаем курсор после маркера
        setTimeout(() => {
            textarea.focus();
            textarea.selectionStart = textarea.selectionEnd = lineStart + 2;
        }, 0);
    };

    // Функция для отслеживания выделения текста
    const handleTextSelection = () => {
        const textarea = textAreaRef.current;
        if (!textarea) return;

        const { selectionStart, selectionEnd } = textarea;

        if (selectionStart !== selectionEnd) {
            setSelection({
                start: selectionStart,
                end: selectionEnd
            });
            setShowToolbar(true);

            // Установка позиции тулбара
            const selection = window.getSelection();
            if (selection.rangeCount > 0) {
                const range = selection.getRangeAt(0);
                const rect = range.getBoundingClientRect();

                setToolbarPosition({
                    top: rect.top - 40,
                    left: rect.left
                });
            }
        } else {
            setShowToolbar(false);
            setSelection(null);
        }
    };

    // Функция для выделения текста 
    const highlightSelectedText = () => {
        if (!selection) return;

        const { start, end } = selection;

        // Проверяем, не перекрывается ли выделение с существующими
        const overlapping = highlights.some(
            highlight =>
                (start >= highlight.start && start < highlight.end) ||
                (end > highlight.start && end <= highlight.end) ||
                (start <= highlight.start && end >= highlight.end)
        );

        if (overlapping) {
            alert('Нельзя создавать перекрывающиеся выделения');
            return;
        }

        const newHighlight = { start, end, style: 'bold' };

        // Добавляем новое выделение и сортируем по начальной позиции
        const newHighlights = [...highlights, newHighlight]
            .sort((a, b) => a.start - b.start);

        setHighlights(newHighlights);
        setShowToolbar(false);
        setSelection(null);
    };

    // Функция для удаления выделения
    const removeHighlight = (highlightIndex) => {
        const updatedHighlights = [...highlights];
        updatedHighlights.splice(highlightIndex, 1);
        setHighlights(updatedHighlights);
    };

    // Функция для сохранения нового FAQ
    const saveFAQ = async () => {
        // Проверка на заполненность обязательных полей
        if (!text.trim()) {
            alert('Введите текст для FAQ!');
            return;
        }

        try {
            setLoading(true);

            // Определение максимального order и добавление 1
            const maxOrder = faqs.length > 0
                ? Math.max(...faqs.map(faq => faq.order || 0))
                : -1;
            const newOrder = maxOrder + 1;

            const response = await axiosInstance.post('/faq', {
                category,
                text,
                highlights,
                order: newOrder
            });

            if (response.data.success) {
                // После успешного создания переходим на страницу категории
                navigate(`/faq/${category}`);
            } else {
                alert('Ошибка при создании FAQ: ' + response.data.error);
                setLoading(false);
            }
        } catch (error) {
            console.error('Ошибка при создании FAQ:', error);
            alert('Ошибка при создании FAQ. Возможно, у вас нет прав доступа.');
            setLoading(false);
        }
    };

    // Функция для форматирования текста с выделенными частями (предпросмотр)
    const formatTextWithHighlights = (text, highlights) => {
        if (!text) {
            return '';
        }

        let parts = [];
        let lastIndex = 0;

        // Если есть выделения, сначала обрабатываем их
        if (highlights && highlights.length > 0) {
            // Сортируем выделения по начальной позиции
            const sortedHighlights = [...highlights].sort((a, b) => a.start - b.start);

            // Создаем новую строку с выделенными частями
            sortedHighlights.forEach((highlight, index) => {
                // Добавляем текст до выделения
                if (highlight.start > lastIndex) {
                    parts.push(text.substring(lastIndex, highlight.start));
                }

                // Добавляем выделенный текст с встроенными стилями
                const highlightedText = text.substring(highlight.start, highlight.end);
                parts.push(`<span style="font-weight:700;color:white;display:inline !important;" class="highlight-bold">${highlightedText}</span>`);

                lastIndex = highlight.end;
            });

            // Добавляем оставшийся текст после последнего выделения
            if (lastIndex < text.length) {
                parts.push(text.substring(lastIndex));
            }
        } else {
            // Если выделений нет, просто используем весь текст
            parts.push(text);
        }

        // Объединяем все части текста
        const fullText = parts.join('');

        // Если нет выделений, обрабатываем только маркеры списка
        if (!highlights || highlights.length === 0) {
            // Разбиваем текст на строки и форматируем каждую
            const lines = text.split('\n');
            return lines.map((line, lineIndex) => {
                if (line.startsWith('• ')) {
                    return (
                        <div key={lineIndex} className="list-item" style={{ display: 'flex', marginBottom: '5px' }}>
                            <span className="list-marker" style={{ marginRight: '5px' }}>•</span>
                            <span className="list-text">{line.substring(2)}</span>
                        </div>
                    );
                }

                // Если строка пустая, возвращаем пустой div с высотой строки
                if (!line.trim()) {
                    return <div key={lineIndex} style={{ height: '1em' }}>&nbsp;</div>;
                }

                return <div key={lineIndex}>{line}</div>;
            });
        }

        // Разбиваем на строки и обрабатываем списки
        const lines = fullText.split('\n');
        return lines.map((line, lineIndex) => {
            // Обрабатываем маркеры списка
            if (line.startsWith('• ')) {
                const markerText = '• ';
                const restOfLine = line.substring(markerText.length);
                return (
                    <div key={lineIndex} className="list-item" style={{ display: 'flex', marginBottom: '5px' }}>
                        <span className="list-marker" style={{ marginRight: '5px' }}>•</span>
                        <span className="list-text" dangerouslySetInnerHTML={{ __html: restOfLine }} />
                    </div>
                );
            }

            // Если строка пустая, возвращаем пустой div с высотой строки
            if (!line.trim()) {
                return <div key={lineIndex} style={{ height: '1em' }}>&nbsp;</div>;
            }

            // Обычная строка с возможными выделениями
            return (
                <div key={lineIndex} dangerouslySetInnerHTML={{ __html: line }} />
            );
        });
    };

    return (
        <>
            {/* Лоадер */}
            {loading && <div className="faq-page-loader"><LoaderInside /></div>}

            <div className="faq-content-container">
                {/* Заголовок страницы с кнопкой назад */}
                <div className="faq-content-header">
                    <button className="back-button" onClick={handleBack}>
                        &lt; Назад
                    </button>
                    <h1>Создать FAQ: {getCategoryTitle()}</h1>
                </div>

                {/* Форма создания FAQ */}
                <div className="admin-edit-panel" style={{ marginTop: '0' }}>
                    <h3 className="admin-edit-header">Создание новой записи FAQ</h3>
                    <div className="admin-edit-form">
                        <label>Текст FAQ:</label>
                        <div className="text-editor">
                            <div className="editor-wrapper">
                                {/* Кнопки форматирования текста */}
                                <div className="editor-toolbar">
                                    <button
                                        type="button"
                                        onClick={addListItem}
                                        className="format-button"
                                    >
                                        Добавить маркер списка
                                    </button>
                                </div>

                                {/* Тулбар для выделения текста */}
                                {showToolbar && (
                                    <div
                                        className="text-selection-toolbar"
                                        style={{
                                            top: toolbarPosition.top + 'px',
                                            left: toolbarPosition.left + 'px'
                                        }}
                                    >
                                        <button onClick={highlightSelectedText}>
                                            Выделить
                                        </button>
                                    </div>
                                )}

                                <textarea
                                    ref={textAreaRef}
                                    value={text}
                                    onChange={(e) => updateText(e.target.value)}
                                    onSelect={handleTextSelection}
                                    placeholder="Введите текст FAQ..."
                                    rows={10}
                                />

                                {/* Превью текста с выделениями */}
                                {text && (
                                    <div className="preview-section">
                                        <h4>Предпросмотр:</h4>
                                        <div className="faq-card-content preview-content">
                                            {formatTextWithHighlights(text, highlights)}
                                        </div>
                                    </div>
                                )}

                                {/* Список выделений */}
                                {highlights.length > 0 && (
                                    <div style={{ marginTop: '10px' }}>
                                        <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.8)', marginBottom: '5px' }}>
                                            Выделенные части (кликните для удаления):
                                        </div>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                                            {highlights.map((highlight, highlightIndex) => (
                                                <button
                                                    key={highlightIndex}
                                                    onClick={() => removeHighlight(highlightIndex)}
                                                    style={{
                                                        background: 'rgba(255,255,255,0.4)',
                                                        border: 'none',
                                                        padding: '3px 8px',
                                                        borderRadius: '3px',
                                                        color: 'white',
                                                        fontSize: '11px',
                                                        cursor: 'pointer',
                                                        fontWeight: 'bold'
                                                    }}
                                                >
                                                    "{text.substring(highlight.start, highlight.end)}"
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="admin-edit-buttons" style={{ marginTop: '20px' }}>
                            <button className="save-button" onClick={saveFAQ}>
                                Сохранить FAQ
                            </button>
                            <button className="cancel-button" onClick={handleBack}>
                                Отмена
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default FAQCreate; 